import { List } from "components";
import { RenderProfile } from "components/render-profile";
import { StatusCell } from "@/components/status/cell";
import { HoursCell } from "@/components/hours/cell";
import { useParams } from "react-router-dom";
import { getUserFields } from "components/user/config";
import { Button } from "ui/components/button";
import { Icon } from "components/index";
import { MENU } from "icons";
import { useState } from "react";
import { useRemovePerformanceReview } from "./mutations";
import { eventBus } from "utils/eventBus";
import PerformanceReviewInputSheet from "./input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "ui/components/dropdown-menu";
import { ConfirmationDialog } from "../../components/confirmation-dialog";
import { PermissionControlledInput } from "permissions/components/input";
import { formatName } from "utils/index";
import { fetchEmployee } from "directus/src/operations";
import { useFetch } from "@/hooks/useFetch";

export default function ReviewCoverageReviews() {
  const { quadId, employeeId } = useParams();
  const [reviews, setReviews] = useState(null);
  const [confirmation, setConfirmation] = useState({
    open: false,
    title: "",
    description: "",
    action: null,
  });
  const [actionLoading, setActionLoading] = useState(false);

  const handleConfirm = async () => {
    if (!confirmation.action) return;
    setActionLoading(true);
    try {
      await confirmation.action();
      setConfirmation({ ...confirmation, open: false });
    } catch (error) {
    } finally {
      setActionLoading(false);
    }
  };

  const { data: employee } = useFetch({
    queryKey: ["reviewee", "employee", employeeId],
    queryFn: () => fetchEmployee(employeeId, {
      fields: ["id", "first_name", "last_name"],
    }),
    enabled: !!employeeId,
  });

  const revieweeName = employee ? formatName(employee) : "Reviewee";

  const removePerformanceReview = useRemovePerformanceReview({
    onSuccess: () => {
      eventBus.emit(
        "refresh:list",
        `performance_reviewcoverage-performance-reviews-${employeeId}-${quadId}`,
      );
    },
  });

  const fields = [
    "id",
    "status",
    "quadrimester",
    getUserFields("reviewee"),
    getUserFields("reviewer"),
    "hours_reviewed",
    "self_nominated",
  ];

  const columns = [
    {
      header: "Reviewer",
      accessorKey: "reviewer",
      cell: ({ getValue }) => <RenderProfile user={getValue()} />,
    },
    {
      header: "Hours Reviewed",
      accessorKey: "hours_reviewed",
      cell: HoursCell,
    },
    {
      header: "Type",
      accessorKey: "self_nominated",
      cell: ({ getValue }) => (getValue() ? "Nominated" : "System"),
    },
    {
      header: "Status",
      accessorKey: "status",
      cell: StatusCell,
    },
    {
      header: "",
      accessorKey: "id",
      cell: ({ row }) => {
        const reviewId = row.original.id;
        const status = row.original.status;
        return (
          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="icon" variant="outline">
                  <Icon icon={MENU} className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <PermissionControlledInput
                  rule="delete:*"
                  collection="performance_review"
                >
                  <DropdownMenuItem
                    onClick={() =>
                      setConfirmation({
                        open: true,
                        title: "Remove Review",
                        description:
                          "Are you sure you want to remove this entry?",
                        action: () =>
                          removePerformanceReview.mutateAsync({ reviewId }),
                      })
                    }
                    disabled={
                      removePerformanceReview.isPending ||
                      status === "submitted"
                    }
                  >
                    Remove
                  </DropdownMenuItem>
                </PermissionControlledInput>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
      sortable: false,
    },
  ];

  const filters = {
    quadrimester: { _eq: quadId },
    reviewee: { _eq: employeeId },
    reviewer: { _neq: employeeId },
  };

  return (
    <>
      <PerformanceReviewInputSheet reviews={reviews} />
      <List
        title={`${revieweeName} - Reviewers`}
        storageKey={`coverage-performance-reviews-${employeeId}-${quadId}`}
        endPoint="performance_review"
        filters={filters}
        columns={columns}
        meta={{ fields }}
        href={null}
        // hidePagination={true}
        showSearch={false}
        onDataChange={setReviews}
      />
      <ConfirmationDialog
        open={confirmation.open}
        onOpenChange={(open) => setConfirmation((prev) => ({ ...prev, open }))}
        title={confirmation.title}
        description={confirmation.description}
        onConfirm={handleConfirm}
        isLoading={actionLoading}
        onCancel={() => setConfirmation((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
}
